import { parseFlagArtifactIdentity } from './internal/components/AgentInsights/components/artifacts/parseArtifactIdentity';
import type { ResourceOrQuery } from './resources';

export { parseFlagArtifactIdentity };

export interface BuildFlagContextArgs {
  projectKey: string;
  flagKey: string;
  name?: string | null;
  url?: string | null;
}

/**
 * Flag artifacts store identity as `{projectKey}/{flagKey}`. Reuse that as the
 * Vega resource id so mention chips, the @-mention picker, prompt context
 * blocks, and the artifact page all share one key. `extractContextFromPrompt`
 * accepts `/` in ids.
 */
export const flagContextId = (projectKey: string, flagKey: string): string => `${projectKey}/${flagKey}`;

/** In-app flag page path. Shared by mention chips and the search payload URL. */
export const flagPagePath = (projectKey: string, flagKey: string): string =>
  `/projects/${encodeURIComponent(projectKey)}/flags/${encodeURIComponent(flagKey)}`;

/**
 * Builds the `ResourceOrQuery` for attaching a feature flag to a Vega
 * conversation. Centralised so the @-mention picker and the flag artifact
 * Ask Vega CTA produce the same payload — `formatFlagEntry` in
 * `agentInsightsUtils` is the reader of these keys.
 */
export const buildFlagContextResource = ({
  projectKey,
  flagKey,
  name,
  url,
}: BuildFlagContextArgs): ResourceOrQuery => ({
  id: flagContextId(projectKey, flagKey),
  type: 'Flag',
  payload: {
    flag_key: flagKey,
    project_key: projectKey,
    name: name ?? undefined,
    url: url ?? undefined,
  },
});

export const flagContextResourceFromArtifact = (artifact: {
  external_id: string;
  title?: string | null;
  url?: string | null;
}): ResourceOrQuery | null => {
  const identity = parseFlagArtifactIdentity(artifact.external_id);
  if (!identity) {
    return null;
  }
  return buildFlagContextResource({
    projectKey: identity.projectKey,
    flagKey: identity.flagKey,
    name: artifact.title,
    url: artifact.url,
  });
};
